import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  StateKey,
  TransferState,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment';
import type {
  BlogCommentCreatePayload,
  BlogCommentCreateResponse,
  BlogCommentsListResponse,
} from './blog-comments.api-model';
import {
  toBlogCommentCreated,
  toBlogCommentsPage,
  type BlogCommentCreated,
  type BlogCommentsPage,
} from './blog-comments.mappers';

/** Clave de `TransferState` de la página 1 del hilo de un slug (B106). */
export function blogCommentsStateKey(slug: string): StateKey<BlogCommentsPage | null> {
  return makeStateKey<BlogCommentsPage | null>('blog-comments:' + slug);
}

/**
 * Costura HTTP de los comentarios públicos (B104). Independiente de
 * `BlogService`, que sigue en mock hasta la costura de posts (P38).
 */
@Injectable({ providedIn: 'root' })
export class BlogCommentsService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Una página del hilo. `null` = 404 (entrada no visible en la API, mismo
   * criterio que `getBySlug`); cualquier otro error se propaga.
   *
   * La página 1 viaja del SSR al navegador por `TransferState` (B106): el
   * transfer cache de Angular no transfiere peticiones con `Authorization`
   * (P39), así que sin esto el cliente volvería a pedir y su primer render
   * podría no coincidir con el del servidor. No se usa `withTransferState`
   * porque ese util descarta a propósito los snapshots `null`, y aquí el `null`
   * del 404 es un resultado que el cliente debe respetar sin volver a pedir.
   */
  list(slug: string, page = 1): Observable<BlogCommentsPage | null> {
    const key = page === 1 ? blogCommentsStateKey(slug) : null;

    if (key && this.isBrowser && this.transferState.hasKey(key)) {
      const snapshot = this.transferState.get(key, null);
      // Se consume una sola vez: volver al mismo slug más tarde pide fresco.
      this.transferState.remove(key);
      return of(snapshot);
    }

    // Sin `per_page`: el default de la API (20) es el del diseño (B104).
    const params = new HttpParams().set('page', page);
    return this.http.get<BlogCommentsListResponse>(this.url(slug), { params }).pipe(
      map(toBlogCommentsPage),
      catchError((err: unknown) =>
        err instanceof HttpErrorResponse && err.status === 404 ? of(null) : throwError(() => err),
      ),
      tap((result) => {
        if (key && !this.isBrowser) this.transferState.set(key, result);
      }),
    );
  }

  /**
   * Envía un comentario. El cuerpo se arma por lista blanca: solo las claves
   * de `BlogCommentCreatePayload`, `parent_id` solo si hay y nunca
   * `subscribe_to_replies` (B114). `website` viaja siempre tal cual (B111).
   * Los errores (422, 429, 404…) se propagan tal cual.
   */
  create(slug: string, payload: BlogCommentCreatePayload): Observable<BlogCommentCreated> {
    const body: BlogCommentCreatePayload = { body: payload.body, website: payload.website };
    if (payload.author_name !== undefined) body.author_name = payload.author_name;
    if (payload.author_email !== undefined) body.author_email = payload.author_email;
    if (payload.parent_id != null) body.parent_id = payload.parent_id;

    return this.http
      .post<BlogCommentCreateResponse>(this.url(slug), body)
      .pipe(map(toBlogCommentCreated));
  }

  private url(slug: string): string {
    return `${environment.apiUrl}/web/blog/posts/${encodeURIComponent(slug)}/comments`;
  }
}
